import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from 'src/common/utils/api';
import { PickedItem } from 'src/data-capture/file-management/drop-zone/SharePointButton';
import { FileUploadInput, UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { invalidateDataUploadFiles } from 'src/data-capture/hooks/file-uploads/invalidateDataUploadFiles';
import { UPLOAD_CONCURRENCY, uploadPool } from 'src/data-capture/hooks/file-uploads/uploadPool';
import { useUploadToStorage } from 'src/data-capture/hooks/file-uploads/useUploadToStorage';

type FileUploadRequest = {
  fileName?: string;
  fileSize: number;
  fileType: string;
  isFolder: boolean;
  isResolved: boolean;
  isTemporary: boolean;
  modelId?: string;
  parentId?: string;
  documentDate?: string | null;
  location?: string;
  uploadToken?: string;
};

type FamilyOneDriveUploadInput = {
  items: PickedItem[];
  modelId?: string;
  documentDate?: string | null;
  parentId?: string | null;
  isResolved?: boolean;
  isTemporary?: boolean;
};

type UseFamilyFileUploadActionsOptions = {
  familyId: string;
};

export const useFamilyFileUploadActions = ({ familyId }: UseFamilyFileUploadActionsOptions) => {
  const queryClient = useQueryClient();
  const { uploadToStorage, extractMetadata } = useUploadToStorage();
  const resource = `families/${familyId}/data-upload-files`;
  const [uploadProgress, setUploadProgress] = useState({ completed: 0, total: 0 });
  const invalidate = () => invalidateDataUploadFiles(queryClient, { familyId });

  const uploadSingleFile = async (fileInput: FileUploadInput): Promise<{ data: UploadedFile }> => {
    const requestInput: FileUploadRequest = {
      fileName: fileInput.fileName || fileInput.file?.name,
      fileSize: fileInput.file?.size || 0,
      fileType: fileInput.file?.type || 'FOLDER',
      isFolder: fileInput.isFolder || false,
      isResolved: fileInput.isResolved || false,
      isTemporary: fileInput.isTemporary || false,
      modelId: fileInput.modelId || undefined,
      parentId: fileInput.parentId,
      documentDate: fileInput.documentDate?.split('T')[0],
    };
    if (fileInput.file) {
      const uploadResult = await uploadToStorage(fileInput.file, { familyId });
      if (!requestInput.documentDate) {
        const metadata = await extractMetadata(
          uploadResult.location,
          fileInput.file,
          uploadResult.uploadToken,
        );
        if (metadata.creationDate) {
          requestInput.documentDate = metadata.creationDate;
        }
      }
      if (uploadResult.location) {
        requestInput.location = uploadResult.location;
        requestInput.uploadToken = uploadResult.uploadToken;
      }
    }
    return api.post<UploadedFile>(resource, requestInput);
  };

  const { mutateAsync: uploadFiles, error: uploadError } = useMutation<
    { data: UploadedFile }[],
    Error,
    FileUploadInput[]
  >({
    mutationFn: (files: FileUploadInput[]) => {
      setUploadProgress({ completed: 0, total: files.length });
      return uploadPool(
        files.map(fileInput => ({
          task: () => uploadSingleFile(fileInput),
          label: fileInput.file?.name ?? fileInput.fileName ?? 'Unknown file',
        })),
        {
          concurrency: UPLOAD_CONCURRENCY,
          context: { familyId },
          onSettled: () => setUploadProgress(p => ({ ...p, completed: p.completed + 1 })),
        },
      );
    },
    onSuccess: () => invalidate(),
  });

  const createFolder = async (name: string, parentId?: string) => {
    await api.post(resource, {
      fileName: name,
      fileSize: 0,
      fileType: 'FOLDER',
      isFolder: true,
      isResolved: true,
      parentId,
    });
    await invalidate();
  };

  const { mutateAsync: uploadOneDriveFiles, isPending: isUploadingOneDrive } = useMutation<
    UploadedFile[],
    Error,
    FamilyOneDriveUploadInput
  >({
    mutationFn: async (input: FamilyOneDriveUploadInput) => {
      const items = input.items.filter(item => item.downloadUrl);
      const results = await uploadPool(
        items.map(item => ({
          task: async () => {
            const { data } = await api.post<UploadedFile[]>(`${resource}/onedrive`, {
              files: [
                {
                  fileName: item.name || 'Unknown',
                  downloadUrl: item.downloadUrl!,
                  fileSize: item.size,
                  createdDateTime: item.createdDateTime,
                },
              ],
              modelId: input.modelId,
              documentDate: input.documentDate?.split('T')[0],
              parentId: input.parentId,
              isTemporary: input.isTemporary,
              isResolved: input.isResolved,
            });
            return data;
          },
          label: item.name || 'Unknown file',
        })),
        { concurrency: UPLOAD_CONCURRENCY, context: { familyId } },
      );
      return results.flat();
    },
    onSuccess: async () => {
      await invalidate();
      await queryClient.invalidateQueries({ queryKey: ['families', familyId, 'all-documents'] });
    },
  });

  return {
    uploadFiles,
    uploadProgress,
    createFolder,
    uploadOneDriveFiles,
    isUploadingOneDrive,
    error: uploadError,
  };
};
